import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Place } from '@/types';
import { TabId, TAB_IDS } from '@/features/NewBenchmarksUI/types/data';

/**
 * Parsed state from the query-string used by the PermitBenchmarking page.
 * Keeps all URL parsing logic in one place so it can be unit-tested and reused.
 */
export const usePermitBenchmarkingURLState = () => {
  const [searchParams] = useSearchParams();

  const name = searchParams.get('name') || null;
  const place_fips = searchParams.get('place_fips') || null;
  const tab = searchParams.get('tab') || null;

  const peers = useMemo((): Place[] => {
    const peersParam = searchParams.get('peers') || '';
    if (!peersParam) return [];

    try {
      return peersParam
        .split(',')
        .filter(Boolean)
        .map((entry) => {
          const parts = entry.split(':');
          const fips = parts.pop() || '';
          const encodedName = parts.join(':');
          if (!encodedName || !fips) return null;
          const placeName = decodeURIComponent(encodedName);
          return { name: placeName, place_fips: fips } as Place;
        })
        .filter((p): p is Place => p !== null);
    } catch (e) {
      console.error('Error parsing peers from URL', e);
      return [];
    }
  }, [searchParams]);

  const selectedPlace = useMemo((): Place | null => {
    if (name && place_fips) {
      return { name: name, place_fips: place_fips } as Place;
    }
    return null;
  }, [name, place_fips]);

  const activeTab = useMemo((): TabId => {
    if (tab && Object.values(TAB_IDS).includes(tab as TabId)) {
      return tab as TabId;
    }
    return TAB_IDS.PEER_COMPARISON;
  }, [tab]);

  return { selectedPlace, peers, activeTab } as const;
}; 